import {
  Puzzle,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
} from 'lucide-react'
import type { PluginHealth } from '@/types/plugins'

interface PluginHealthSidebarProps {
  plugins: PluginHealth[]
  loading: boolean
  selectedPlugin: string | null
  onSelectPlugin: (plugin: string | null) => void
}

function StatusIcon({ status }: { status: PluginHealth['status'] }) {
  switch (status) {
    case 'healthy':
      return <CheckCircle className="w-3 h-3 text-emerald-400" />
    case 'degraded':
      return <AlertTriangle className="w-3 h-3 text-yellow-400" />
    case 'error':
      return <XCircle className="w-3 h-3 text-red-400" />
    case 'disabled':
      return <XCircle className="w-3 h-3 text-gray-500" />
    default:
      return <CheckCircle className="w-3 h-3 text-gray-500" />
  }
}

function PluginItem({
  plugin,
  isSelected,
  onClick,
}: {
  plugin: PluginHealth
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
        isSelected
          ? 'bg-accent/10 text-accent border border-accent/30'
          : 'hover:bg-surface-hover text-text-muted'
      }`}
    >
      <StatusIcon status={plugin.status} />
      <span className="flex-1 text-sm truncate">{plugin.name}</span>
      <span className="text-xs text-text-muted">{plugin.toolCount}</span>
    </button>
  )
}

export default function PluginHealthSidebar({
  plugins,
  loading,
  selectedPlugin,
  onSelectPlugin,
}: PluginHealthSidebarProps) {
  const healthyCount = plugins.filter((p) => p.status === 'healthy').length
  const totalCount = plugins.length
  const totalTools = plugins.reduce((acc, p) => acc + p.toolCount, 0)

  if (loading && plugins.length === 0) {
    return (
      <div className="bg-surface border border-surface-border rounded-xl p-4 h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    )
  }

  return (
    <div className="bg-surface border border-surface-border rounded-xl overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-surface-border">
        <div className="flex items-center gap-2 mb-2">
          <Puzzle className="w-4 h-4 text-accent" />
          <span className="text-sm font-semibold text-text">Plugins</span>
        </div>
        {/* Show All option */}
        <button
          onClick={() => onSelectPlugin(null)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
            selectedPlugin === null
              ? 'bg-accent/10 text-accent border border-accent/30'
              : 'hover:bg-surface-hover text-text-muted'
          }`}
        >
          <CheckCircle className="w-3 h-3" />
          <span className="flex-1 text-sm">All Plugins</span>
          <span className="text-xs">{totalTools}</span>
        </button>
      </div>

      {/* Plugin List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {plugins.map((plugin) => (
          <PluginItem
            key={plugin.name}
            plugin={plugin}
            isSelected={selectedPlugin === plugin.name}
            onClick={() =>
              onSelectPlugin(selectedPlugin === plugin.name ? null : plugin.name)
            }
          />
        ))}
      </div>

      {/* Stats */}
      <div className="p-3 border-t border-surface-border">
        <div className="flex justify-between text-xs text-text-muted mb-1">
          <span>Healthy</span>
          <span className="text-emerald-400">
            {healthyCount}/{totalCount}
          </span>
        </div>
        <div className="flex justify-between text-xs text-text-muted">
          <span>Total Tools</span>
          <span className="text-text">{totalTools}</span>
        </div>
      </div>
    </div>
  )
}
